import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { useSelector, useDispatch } from 'react-redux';
import { BASE_URL } from '../../utils/Constants';
import { userUpdateProfileSuccess } from '../reducers/userInfo/slice';

/**
 * Socket context for managing WebSocket connections
 */
const SocketContext = createContext(null);

/**
 * Socket Provider component that manages the WebSocket connection
 * Automatically connects when user is authenticated and joins appropriate rooms
 */
export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const dispatch = useDispatch();

  // Get user info from Redux store
  const user = useSelector((state) => state.userInfo?.user);
  const token = user?.token;
  const userId = user?.userId || user?._id; // Handle both cases
  const userRole = user?.role?.[0] || user?.user?.role?.[0];

  // Use a ref to store the latest user object to avoid stale closures in socket callbacks
  // without triggering re-connections when user details change
  const userRef = useRef(user);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Connect to socket when user is authenticated
  useEffect(() => {
    if (!token) {
      return;
    }

    // Create socket connection
    const socketUrl = BASE_URL.replace('/api', '').replace(/\/$/, '');
    const newSocket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      withCredentials: true,
      auth: {
        token: token,
      },
    });

    // Connection event handlers
    newSocket.on('connect', () => {
      console.log('Socket connected:', newSocket.id);
      setIsConnected(true);

      // Join rooms based on user role and ID
      newSocket.emit('join', {
        role: userRole,
        userId: userId,
      });
    });

    newSocket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message);
      setIsConnected(false);
    });

    // Handle user status updates (e.g., admin approved seller/brand)
    newSocket.on('userStatusUpdate', (data) => {
      console.log('Received user status update:', data);

      if (data.user) {
        // Update Redux directly with fresh user data from socket
        // Use userRef.current to get the latest user state
        dispatch(
          userUpdateProfileSuccess({
            ...userRef.current,
            ...data.user,
            token: token, // Ensure token persists
          }),
        );
      }
    });

    setSocket(newSocket);

    // Cleanup on unmount or when dependencies change
    return () => {
      newSocket.disconnect();
      setSocket(null);
      setIsConnected(false);
    };
  }, [token, userRole, userId, dispatch]);

  // Method to manually emit events
  const emit = useCallback(
    (event, data) => {
      if (socket && isConnected) {
        socket.emit(event, data);
      }
    },
    [socket, isConnected],
  );

  // Context value
  const value = {
    socket,
    isConnected,
    emit,
  };

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};

/**
 * Custom hook to access socket context
 */
export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export default SocketContext;
